import React, { useMemo, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams, useNavigate } from 'react-router-dom'
import './Product.scss'
import './Shopify.scss'
import { getMediumImageUrl } from '../utils/imageLoader'
import { motion } from 'framer-motion'



const Product = ({ products = [], productsLoaded = false }) => {
    const { id } = useParams()
    const navigate = useNavigate()

    const product = useMemo(
        () => products.find((p) => String(p.url) === String(id)),
        [products, id]
    )

    const images = useMemo(() => (
        product?.photos?.length ? product.photos : product?.src ? [product.src] : []
    ), [product])

    const optimizedImages = useMemo(() => images.map((img) => {
        return getMediumImageUrl(img, { format: 'jpg', quality: 85 });
    }), [images])

    const [index, setIndex] = useState(0)
    const [imageLoaded, setImageLoaded] = useState(false)
    const [imageDimensions, setImageDimensions] = useState(null)
    const [allImagesLoaded, setAllImagesLoaded] = useState(false)

    const firstImageAsset = useMemo(() => {
        if (product?.photos?.length > 0) return product.photos[0];
        return product?.asset;
    }, [product]);

    const firstImageDimensions = useMemo(() => {
        return firstImageAsset?.fields?.file?.details?.image;
    }, [firstImageAsset]);

    useEffect(() => {
        if (productsLoaded && !product) {
            navigate('/', { replace: true })
        }
    }, [navigate, product, productsLoaded])

    // Preload all images before showing the first one
    useEffect(() => {
        if (!optimizedImages.length) {
            setAllImagesLoaded(true);
            return;
        }

        setAllImagesLoaded(false); // Reset on product change
        let loadedCount = 0;
        optimizedImages.forEach(src => {
            const img = new Image();
            const increment = () => {
                loadedCount++;
                if (loadedCount === optimizedImages.length) {
                    setAllImagesLoaded(true);
                }
            };
            img.onload = increment;
            img.onerror = increment; // Count as loaded even on error to avoid infinite loading
            img.src = src;
        });
    }, [optimizedImages])

    // Load image and get intrinsic dimensions
    useEffect(() => {
        if (!optimizedImages[index]) return;

        const img = new Image();
        img.onload = () => {
            setImageDimensions({
                width: img.naturalWidth,
                height: img.naturalHeight
            });
        };
        img.src = optimizedImages[index];
    }, [optimizedImages, index])

    const handleClick = () => {
        if (!optimizedImages.length) return
        setImageLoaded(false)
        setImageDimensions(null)
        setIndex((prev) => (prev + 1) % optimizedImages.length)
    }

    const handleBackgroundClick = (e) => {
        const el = e.target
        if (el && el.closest) {
            // don't navigate when clicking the image or interactive controls
            if (el.closest('.viewer img')) return
            if (el.closest('.productCaption')) return
            if (el.closest('button, a, input, select, textarea, .shopify-addtocard-button')) return
        }
        navigate(-1)
    }

    // Load Shopify Buy Button and render product component into the container
    useEffect(() => {
        if (!allImagesLoaded || !product?.shopifyBuyId || !product?.productComponentId) {
            console.log('Skipping Shopify Buy Button: missing IDs or not loaded', { shopifyBuyId: product?.shopifyBuyId, productComponentId: product?.productComponentId, allImagesLoaded });
            return;
        }

        const scriptURL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js';
        const containerId = `product-component-${product.productComponentId}`;

        let removeToggleListener = null;

        function ShopifyBuyInit() {
            if (!window.ShopifyBuy) return;
            const client = window.ShopifyBuy.buildClient({
                domain: 'bxi4sc-dj.myshopify.com',
                storefrontAccessToken: 'ce06086628522497d86426cd11e4c0f7',
            });
            window.ShopifyBuy.UI.onReady(client).then((ui) => {
                const node = document.getElementById(containerId);
                if (!node) {
                    console.error('Shopify container not found:', containerId);
                    return;
                }

                // Read CSS variables from the container
                const styles = getComputedStyle(node);
                const getVar = (name) => styles.getPropertyValue(name).trim();

                node.innerHTML = '';
                ui.createComponent('product', {
                    id: String(product.shopifyBuyId),
                    node: document.getElementById(containerId),
                    moneyFormat: '%E2%82%AC%7B%7Bamount_with_comma_separator%7D%7D',
                    options: {
                        product: {
                            styles: {
                                product: {
                                    '@media (min-width: 601px)': {
                                        'max-width': 'calc(25% - 20px)',
                                        'margin-left': '20px',
                                        'margin-bottom': '50px',
                                    },
                                },
                                quantityContainer: { // doesnt work 
                                    'display': 'none',
                                },
                                button: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'font-size': getVar('--shopify-button-font-size'),
                                    // 'padding': getVar('--shopify-button-padding'),
                                    'background-color': 'transparent',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                    'padding': '0',
                                    'cursor': 'none',
                                    // 'text-decoration': 'underline',

                                    // 'border': '1px solid #5a0135',

                                    //'border-radius': getVar('--shopify-button-border-radius'),
                                    ':hover': {
                                        'background-color': 'transparent',
                                        'cursor': 'none',
                                        'text-decoration': 'underline',
                                        'text-decoration-color': '#5a0135 !important',
                                    },
                                    ':focus': {
                                        'background-color': 'transparent',
                                    },
                                    ':disabled': {
                                        'background-color': 'transparent',
                                        'background': 'transparent',
                                        'box-shadow': 'none',
                                        'border': 'none',
                                        'color': '#5a0135',
                                        'text-decoration': 'line-through',
                                    },
                                },
                                buttonWrapper: {
                                    // 'pointer-events': 'none',
                                    'margin': '0 !important',
                                    'padding': '0 !important',
                                    'width': 'fit-content !important',
                                    'float': 'right !important',
                                    'text-align': 'right !important',
                                    'background-color': 'rgba(0,0,0,0)', // Ensure its not blocking
                                    'display': 'inline-block !important',
                                },
                                quantityInput: {
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'padding-top': '14.5px',
                                    'padding-bottom': '14.5px',
                                    'color': '#5a0135',
                                },
                            },
                            googleFonts: ['Josefin Sans:700'],
                            contents: {
                                img: false,
                                title: false,
                                price: false,
                            },
                            text: {
                                button: 'ajouter au panier',
                                outOfStock: 'ajouter au panier',
                            },
                        },
                        productSet: {
                            styles: {
                                products: {
                                    '@media (min-width: 601px)': {
                                        'margin-left': '-20px',
                                    },
                                },
                            },
                        },
                        modalProduct: {
                            contents: {
                                img: false,
                                imgWithCarousel: true,
                                button: false,
                                buttonWithQuantity: true,
                            },
                            styles: {
                                product: {
                                    '@media (min-width: 601px)': {
                                        'max-width': '100%',
                                        'margin-left': '0px',
                                        'margin-bottom': '0px',
                                    },
                                },
                                button: {
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'padding-top': '14.5px',
                                    'padding-bottom': '14.5px',
                                    'font-family': getVar('--shopify-button-font-family') + ' !important',
                                    'background-color': 'transparent',
                                    'color': '#5a0135',
                                    'border': '1px solid #5a0135',
                                    ':hover': {
                                        'background-color': 'transparent',
                                    },
                                    ':focus': {
                                        'background-color': 'transparent',
                                    },
                                    ':disabled': {
                                        'background-color': 'transparent',
                                        'background': 'transparent',
                                        'box-shadow': 'none',
                                        'border': 'none',
                                        'color': '#5a0135',
                                    },
                                    'border-radius': getVar('--shopify-button-border-radius'),
                                },
                                quantityInput: {
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'padding-top': '14.5px',
                                    'padding-bottom': '14.5px',
                                    'color': '#5a0135',
                                },
                            },
                            googleFonts: ['Josefin Sans:700'],
                            text: {
                                button: 'ajouter au panier',
                                outOfStock: 'ajouter au panier',
                            },
                        },
                        option: {
                            styles: {
                                wrapper: {
                                    'border-radius': getVar('--shopify-button-border-radius'),
                                    'background-color': getVar('--shopify-option-wrapper-bg'),
                                },
                                select: {
                                    'color': '#5a0135',
                                },
                            },
                        },
                        cart: {
                            startOpen: false,
                            popup: false,
                            contents: {
                                close: true,
                            },
                            styles: {
                                header: {
                                    close: {
                                        'color': '#5a0135',
                                        'background-color': 'transparent',
                                        'border': 'none',
                                        'outline': 'none',
                                        'box-shadow': 'none',
                                        'font-family': '"Josefin Sans", sans-serif',
                                        ':hover': {
                                            'color': '#5a0135',
                                            'background-color': 'transparent',
                                        },
                                        ':focus': {
                                            'color': '#5a0135',
                                            'background-color': 'transparent',
                                            'outline': 'none',
                                            'box-shadow': 'none',
                                        },
                                        ':active': {
                                            'outline': 'none',
                                            'box-shadow': 'none',
                                            'background-color': 'transparent',
                                        },
                                        svg: {
                                            fill: '#5a0135',
                                        },
                                    },
                                },
                                base: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                cart: {
                                    'background-color': '#FFFFFF',
                                    'box-shadow': 'none',
                                    'font-weight': '700',
                                    // 'margin': '3rem',
                                    'height': 'calc(100% - 4.85rem)', // 7
                                    'width': 'calc(100% - 4.40rem)',
                                    'margin-top': '2.41rem', // 5 
                                    'margin-right': '2.5rem',


                                    '@media (max-width: 768px)': { // not working
                                        'margin-right': '1.8rem',

                                    },

                                    // 'box-sizing': 'border-box',
                                    /* 'right': '6rem',
                                    'width': 'calc(100% - 6rem)',
                                    'padding-bottom': '2rem', */
                                },
                                title: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                lineItems: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                footer: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                subtotalText: {
                                    'color': '#5a0135 !important',
                                    'font-weight': '700',
                                    'font-family': '"Josefin Sans", sans-serif !important',
                                },
                                total: {
                                    'color': '#5a0135 !important',
                                    'font-weight': '700',
                                    'font-family': '"Josefin Sans", sans-serif !important',
                                },
                                subtotal: {
                                    'color': '#5a0135 !important',
                                    'font-weight': '700',
                                    'font-family': '"Josefin Sans", sans-serif !important',
                                },
                                notice: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                empty: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'color': '#5a0135',
                                    'font-weight': '700',
                                },
                                button: {
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'padding-top': '10px',
                                    'padding-bottom': '10px',
                                    'font-family': '"Josefin Sans", sans-serif !important',
                                    'font-weight': '700',
                                    ':hover': {
                                        'background-color': 'transparent',
                                    },
                                    'background-color': getVar('--shopify-cart-button-bg'),
                                    'color': '#5a0135',

                                    'border': '1px solid #5a0135',
                                    ':focus': {
                                        'background-color': getVar('--shopify-cart-button-hover-bg'),
                                    },
                                    ':disabled': {
                                        'background-color': 'transparent',
                                        'background': 'transparent',
                                        'box-shadow': 'none',
                                        'border': 'none',
                                        'color': '#5a0135',
                                    },
                                    'border-radius': getVar('--shopify-button-border-radius'),
                                },
                            },
                            googleFonts: ['Josefin Sans:700'],
                            text: {
                                title: 'panier',
                                total: 'total',
                                button: 'COMMANDER',
                                outOfStock: 'sold out',
                                notice: 'les frais de port sont calculés au checkout',
                            },
                        },
                        // Show only the decrement (minus) control in cart line items
                        lineItem: {
                            contents: {
                                quantity: true,
                            },
                            styles: {
                                title: {
                                    'font-size': '16px',
                                    'font-weight': '700',
                                },
                                // keep the quantity container visible but hide the numeric input
                                quantity: {},
                                quantityInput: {
                                    display: 'none',
                                },
                                // hide the increment (plus) button
                                quantityIncrement: {
                                    display: 'none',
                                },
                                // show the decrement (minus) button
                                quantityDecrement: {
                                    display: 'inline-block',
                                    'color': '#5a0135',
                                    'background-color': 'transparent',
                                    'border': 'none',
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'font-weight': '700',
                                    ':hover': {
                                        'background-color': 'transparent',
                                    },
                                    ':focus': {
                                        'background-color': 'transparent',
                                    },
                                    svg: {
                                        fill: '#5a0135',
                                    },
                                },
                            },
                        },
                        toggle: {
                            contents: {
                                count: false,
                                icon: false,
                                title: true,
                            },
                            text: {
                                title: 'panier',
                            },
                            styles: {
                                // PANIER BUTTON HIER
                                toggle: {
                                    'position': 'fixed',
                                    'top': '0',
                                    'right': '0',
                                    'padding-right': getVar('--padding-right'),
                                    'padding-top': getVar('--padding-top'),

                                    'padding-left': '0',
                                    'padding-bottom': '0',

                                    /*    '@media (max-width: 768px)': {
                                           'padding-left': '4rem',
   
                                           'padding-top': '1.5rem',
   
                                       }, */




                                    // 'top': '50px',
                                    //'right': '50px',
                                    'background-color': 'transparent',
                                    'border': 'none',
                                    // 'width': '200px',
                                    //  'height': '200px',
                                    'z-index': '1',
                                    /*  'width': '100vw',
                                     'height': '100vh', */
                                    //'cursor': 'none',
                                    'font-weight': '700',
                                    //'padding': getVar('--shopify-button-padding'), // change the positon of panier button
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'cursor': 'none',
                                    ':hover': {
                                        'background-color': 'transparent',
                                        'cursor': 'none',
                                        'text-decoration': 'underline',
                                        'text-decoration-color': '#5a0135 !important',
                                    },
                                    ':focus': {
                                        'background-color': 'transparent',
                                    },

                                },
                                //bouton PANIER
                                title: {
                                    'font-family': '"Josefin Sans", sans-serif',
                                    'font-size': getVar('--shopify-button-font-size'),
                                    'color': '#5a0135',  //test
                                    /*  'color': 'blue', */
                                    'fontWeight': 700,

                                },
                                count: {
                                    'display': 'none',
                                },
                            },
                            googleFonts: ['Josefin Sans:700'],
                        },
                    },
                });




            }).catch((error) => {
                console.error('Shopify UI initialization error:', error);
            });
        }

        if (window.ShopifyBuy && window.ShopifyBuy.UI) {
            ShopifyBuyInit();
        } else {
            const script = document.createElement('script');
            script.async = true;
            script.src = scriptURL;
            script.onload = ShopifyBuyInit;
            script.onerror = () => console.error('Failed to load Shopify Buy Button script');
            (document.head || document.body).appendChild(script);
        }

        return () => {
            document.getElementById(containerId); // Removing unused var 'node'
            if (removeToggleListener) removeToggleListener();
        };
    }, [product, allImagesLoaded]);

    if (!product) {
        return (
            <div className='productPage'>
                <div className='notFound'>
                    {productsLoaded ? 'Redirecting...' : 'Loading...'}
                </div>
            </div>
        )
    }

    const captionParts = product.caption.split(' — ')
    const priceIndex = captionParts.findIndex(part => part.includes('€'))
    const mainInfoParts = priceIndex >= 0 ? captionParts.slice(0, priceIndex) : captionParts.slice(0, -1)
    const priceInfo = priceIndex >= 0 ? captionParts[priceIndex] : captionParts[captionParts.length - 1]

    const findPart = (keywords) => {
        const lower = keywords.map(k => k.toLowerCase())
        const idx = mainInfoParts.findIndex(p => lower.some(k => p.toLowerCase().includes(k)))
        if (idx === -1) return null
        return mainInfoParts[idx]
    }

    const orderedInfoCandidates = [
        product.titre || findPart(['titre']),
        product.taille || findPart(['taille', 'cm', 'size']),
        product.type || findPart(['type', 'cat', 'categorie', 'category']),
        product.matiere || findPart(['matiere', 'matière', 'material']),
        product.teinture || findPart(['teinture', 'teint', 'dyed']),
    ]
    const seen = new Set()
    const orderedMainInfo = orderedInfoCandidates.filter(Boolean).filter(item => {
        const key = item.trim()
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })

    const pageTitle = product?.titre ? `${product.titre} | Marguerite Soulier` : 'Marguerite Soulier'
    const pageDescription = [product?.titre, product?.taille, product?.type, product?.matiere]
        .filter(Boolean).join(' — ')
    const pageUrl = `https://margueritesoulier.fr/${product?.url}`
    const pageImage = product?.photos?.[0]?.fields?.file?.url
        ? `https:${product.photos[0].fields.file.url}`
        : (product?.src || '')

    return (
        <div className='productPage' onClick={handleBackgroundClick}>
            <Helmet>
                <title>{pageTitle}</title>
                <link rel="canonical" href={pageUrl} />
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:description" content={pageDescription} />
                {pageImage && <meta property="og:image" content={pageImage} />}
            </Helmet>

            {product?.productComponentId && (
                <div
                    id={`product-component-${product.productComponentId}`}
                    className='shopify-addtocard-button'

                ></div>
            )}

            <div
                className='viewer'
                style={imageDimensions ? {
                    '--aspect-ratio': `${imageDimensions.width} / ${imageDimensions.height}`,
                    '--original-width': imageDimensions.width
                } : firstImageDimensions ? {
                    '--aspect-ratio': `${firstImageDimensions.width} / ${firstImageDimensions.height}`,
                    '--original-width': firstImageDimensions.width
                } : {}}
            >
                {!allImagesLoaded && (
                    <div className='loadingTag'>chargement...</div>
                )}
                {allImagesLoaded && optimizedImages.length > 0 && (
                    <motion.img
                        key={index}
                        src={optimizedImages[index]}
                        alt={product.titre || 'Artwork'}
                        {...(imageDimensions?.width && { width: imageDimensions.width })}
                        {...(imageDimensions?.height && { height: imageDimensions.height })}
                        onClick={(e) => { e.stopPropagation(); handleClick(); }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: imageLoaded ? 1 : 0 }}
                        transition={{ duration: 0.1 }}
                        onLoad={() => setImageLoaded(true)}

                    />
                )}
            </div>
            <motion.div
                className='productCaption'
                initial={{ opacity: 0 }}
                animate={{ opacity: allImagesLoaded ? 1 : 0 }}
                transition={{ duration: 0.1 }}
            >
                {orderedMainInfo.map((line, i) => (
                    <div key={i}>{line}</div>
                ))}
                <div >
                    {priceInfo}
                    <span className={`stockDot ${product.stock ? 'inStock' : 'outStock'}`} />
                </div>
            </motion.div>

            {/*  <div className='cartButton'>
                <p>cart</p>
            </div>
 */}

            {/*  <div className='nextButton' onClick={handleClick}>cv
                <p>»</p>
            </div> */}
            {/*   <div className='closeButton' onClick={() => navigate(-1)}>
                <p>⤫</p>

            </div> */}
        </div>
    )
}

export default Product